import React, { useEffect } from 'react';
import { safeRequestIdleCallback, safeSetFetchPriority } from '../utils/iosPolyfills';

interface WebVitalsOptimizerProps {
  children: React.ReactNode;
}

const WebVitalsOptimizer: React.FC<WebVitalsOptimizerProps> = ({ children }) => {
  useEffect(() => {
    // Preload critical resources
    const preloadCriticalResources = () => {
      // Preload hero images for LCP optimization
      const heroImages = [
        '/assets/images/mobile mockup1.webp',
        '/assets/images/black Mastercard.webp',
        '/assets/images/blue-light Mastercard.webp'
      ];

      heroImages.forEach(src => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        // Set fetchpriority for critical images (with iOS Safari fallback)
        safeSetFetchPriority(link, 'high');
        document.head.appendChild(link);
      });
    };

    // Optimize font loading for CLS prevention
    const optimizeFontLoading = () => {
      const fontPreloads = [
        'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
        'https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrJJfecnFHGPc.woff2'
      ];

      fontPreloads.forEach(href => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'font';
        link.type = 'font/woff2';
        link.crossOrigin = 'anonymous';
        link.href = href;
        document.head.appendChild(link);
      });
    };

    // Defer non-critical JavaScript for INP optimization
    const deferNonCriticalJS = () => {
      // Defer analytics and tracking scripts
      const scripts = document.querySelectorAll('script[data-defer="true"]');
      scripts.forEach(script => {
        const newScript = document.createElement('script');
        newScript.src = script.getAttribute('src') || '';
        newScript.async = true;
        newScript.defer = true;
        document.head.appendChild(newScript);
      });
    };

    // Optimize images for better LCP
    const optimizeImages = () => {
      const images = document.querySelectorAll('img[data-optimize="true"]');
      images.forEach((img: Element) => {
        const imgElement = img as HTMLImageElement;
        
        // Add loading="lazy" for non-critical images
        if (!imgElement.hasAttribute('data-critical')) {
          imgElement.loading = 'lazy';
        }
        
        // Add decoding="async" for better performance
        imgElement.decoding = 'async';
        
        // Set fetchpriority for critical images (with iOS Safari fallback)
        if (imgElement.hasAttribute('data-critical')) {
          safeSetFetchPriority(imgElement, 'high');
        }
      });
    };

    // Run optimizations
    preloadCriticalResources();
    optimizeFontLoading();
    
    // Defer non-critical optimizations (with iOS Safari fallback)
    safeRequestIdleCallback(() => {
      deferNonCriticalJS();
      optimizeImages();
    });

    // Monitor and report Web Vitals (with iOS Safari compatibility check)
    if (process.env.NODE_ENV === 'development') {
      // Only load web-vitals in development to avoid iOS compatibility issues
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        try {
          getCLS(console.log);
          getFID(console.log);
          getFCP(console.log);
          getLCP(console.log);
          getTTFB(console.log);
        } catch (error) {
          console.warn('Web Vitals monitoring failed (likely iOS Safari compatibility issue):', error);
        }
      }).catch((error) => {
        console.warn('Failed to load web-vitals library:', error);
      });
    }
  }, []);

  return <>{children}</>;
};

export default WebVitalsOptimizer;